import React from "react";
import {
  AbsoluteFill,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { useCopy } from "../copy";
import { STORY_BRAND } from "../data/brands";
import { FLOAT_SHADOW, LOOK, SAFE, TYPE } from "../look";
import { useStage } from "../stage";
import { Words } from "../ui/Words";
import { Sfx } from "../../b2b/ui/Sfx";

/**
 * Scene 1 — the hook. A group-chat moment everybody has lived: a friend
 * asks "where did you get it?" and you send the link. That exchange is the
 * whole product (the /vs pages call it "where recommendations happen").
 *
 * Frame 0 is already a composed image (chat + first message) so the
 * paused first frame works as a poster.
 */

const CARD_W = 820;
const BUBBLE_FONT = 40;

const usePop = (at: number) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  return spring({
    frame: frame - at,
    fps,
    config: { damping: 14, stiffness: 240, mass: 0.6 },
  });
};

const Bubble: React.FC<{
  side: "in" | "out";
  at: number;
  children: React.ReactNode;
}> = ({ side, at, children }) => {
  const p = usePop(at);
  const out = side === "out";
  return (
    <div
      style={{
        alignSelf: out ? "flex-end" : "flex-start",
        maxWidth: 600,
        padding: "22px 30px",
        borderRadius: 40,
        borderBottomRightRadius: out ? 10 : 40,
        borderBottomLeftRadius: out ? 40 : 10,
        background: out ? LOOK.blue : LOOK.mist,
        color: out ? LOOK.white : LOOK.ink,
        fontFamily: LOOK.body,
        fontWeight: LOOK.medium,
        fontSize: BUBBLE_FONT,
        lineHeight: 1.2,
        letterSpacing: -0.5,
        transformOrigin: out ? "100% 100%" : "0% 100%",
        scale: at <= 0 ? 1 : p,
        opacity: at <= 0 ? 1 : Math.min(1, p * 2),
      }}
    >
      {children}
    </div>
  );
};

const Typing: React.FC<{ from: number; to: number }> = ({ from, to }) => {
  const frame = useCurrentFrame();
  if (frame < from || frame >= to) return null;
  return (
    <div
      style={{
        alignSelf: "flex-end",
        display: "flex",
        gap: 10,
        padding: "26px 30px",
        borderRadius: 40,
        borderBottomRightRadius: 10,
        background: LOOK.sky,
      }}
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            width: 16,
            height: 16,
            borderRadius: 16,
            background: LOOK.blue,
            opacity: 0.35 + 0.65 * Math.max(0, Math.sin((frame - from) / 2.2 - i * 0.9)),
          }}
        />
      ))}
    </div>
  );
};

export const Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const copy = useCopy();
  const stage = useStage();

  const REPLY_AT = 36;
  const HEADLINE_AT = 54;

  // Card nudges up to make room for the headline.
  const lift = interpolate(frame, [HEADLINE_AT - 4, HEADLINE_AT + 10], [0, -120], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const card = (
        <div
          style={{
            width: CARD_W,
            borderRadius: 56,
            background: LOOK.white,
            boxShadow: FLOAT_SHADOW,
            padding: "30px 36px 40px",
            display: "flex",
            flexDirection: "column",
            gap: 18,
            flex: "none",
            translate: stage.shape === "square" ? `0 ${lift}px` : undefined,
          }}
        >
          {/* Chat header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 20,
              paddingBottom: 22,
              marginBottom: 6,
              borderBottom: `2px solid ${LOOK.mist}`,
            }}
          >
            <Img
              src={staticFile("images/scene1/2.jpg")}
              style={{ width: 72, height: 72, borderRadius: 72, objectFit: "cover" }}
            />
            <div>
              <div
                style={{
                  fontFamily: LOOK.display,
                  fontWeight: LOOK.bold,
                  fontSize: 38,
                  color: LOOK.ink,
                }}
              >
                Léa
              </div>
              <div style={{ fontFamily: LOOK.body, fontSize: 26, color: LOOK.green }}>
                ● {copy.hook.status}
              </div>
            </div>
          </div>

          <Bubble side="in" at={0}>
            {copy.hook.incoming1}
          </Bubble>
          <Bubble side="in" at={12}>
            {copy.hook.incoming2}
          </Bubble>
          <Typing from={21} to={REPLY_AT} />
          {frame >= REPLY_AT ? (
            <Bubble side="out" at={REPLY_AT}>
              {/* Link preview — the same tote the rest of the video follows. */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 20,
                  padding: 12,
                  margin: "-6px -12px 16px",
                  borderRadius: 26,
                  background: "rgba(255,255,255,0.14)",
                }}
              >
                <div
                  style={{
                    position: "relative",
                    width: 112,
                    height: 112,
                    borderRadius: 18,
                    overflow: "hidden",
                    background: LOOK.white,
                    flex: "none",
                  }}
                >
                  <Img
                    src={staticFile("Order_confirmed.png")}
                    style={{
                      position: "absolute",
                      width: 786 * 0.24,
                      left: -128 * 0.24 + 0,
                      top: -170 * 0.24,
                    }}
                  />
                </div>
                <div style={{ lineHeight: 1.15 }}>
                  <div style={{ fontWeight: LOOK.semi, fontSize: 34 }}>
                    {copy.hook.linkTitle}
                  </div>
                  <div style={{ fontSize: 28, opacity: 0.75 }}>{STORY_BRAND.label}</div>
                </div>
              </div>
              {copy.hook.reply} 👇
            </Bubble>
          ) : null}
        </div>
  );

  // Chat sounds: two bubbles received, the reply sent.
  const cues = (
    <>
      <Sfx sound="bubble-in" at={0} />
      <Sfx sound="bubble-in" at={12} />
      <Sfx sound="bubble-out" at={REPLY_AT} />
    </>
  );

  const headline = (
    <Words
      text={copy.hook.headline}
      start={HEADLINE_AT}
      fontSize={TYPE.headline}
      accents={{ [copy.hook.headlineAccent]: LOOK.blue }}
      align={stage.shape === "wide" ? "left" : "center"}
    />
  );

  if (stage.shape === "square") {
    // Master: card centred, lifts to make room for the headline below.
    return (
      <AbsoluteFill style={{ background: LOOK.sky }}>
        <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 40 }}>
          {card}
        </AbsoluteFill>
        <AbsoluteFill
          style={{ justifyContent: "flex-end", alignItems: "center", paddingBottom: 88 }}
        >
          {headline}
        </AbsoluteFill>
        {cues}
      </AbsoluteFill>
    );
  }

  // Tall: chat above the headline. Wide: chat left, headline right. The
  // headline's slot is reserved up front so the card never jumps.
  const wide = stage.shape === "wide";
  return (
    <AbsoluteFill
      style={{
        background: LOOK.sky,
        flexDirection: wide ? "row" : "column",
        alignItems: "center",
        justifyContent: "center",
        gap: wide ? 100 : 90,
      }}
    >
      {card}
      <div
        style={{
          // Wide: room for "Tu recommandes" on one line. Tall: wraps in two.
          width: wide ? 800 : stage.w - 2 * SAFE,
          height: wide ? undefined : 220,
          flex: "none",
        }}
      >
        {headline}
      </div>
      {cues}
    </AbsoluteFill>
  );
};
